<template>
  <div
    class="w-full h-full flex flex-col pt-4 px-3 pb-0! tablet-h:p-[14px] tablet-h:pr-[64px] desktop:pr-[14px] relative"
    :style="componentStyles"
  >
    <div
      class="font-semibold font-primary text-[18px] leading-[30px] text-gray-800 mb-5 tablet-h:pl-10"
    >
      <template v-if="result">
        {{ result?.searchItems.total }} {{ resultCopy }} for "{{
          route.query.query
        }}"
      </template>
      <template v-else>Searching...</template>
    </div>

    <Table.Base
      :id="TableTypes.SearchFiles"
      class="flex flex-col h-full overflow-hidden tablet-h:pl-10"
    >
      <Table.Headers>
        <Table.Header class="cursor-pointer" @click="handleSort('name')">
          <span class="font-primary">Name</span>
          <Icon
            name="ArrowUp"
            :size="12"
            class="stroke-gray-800 ml-2"
            :class="headerIconClasses"
          />
        </Table.Header>
        <Table.Header class="hidden tablet-h:flex w-[200px]">
          <span class="font-primary">Dacr Timestamp</span>
        </Table.Header>
      </Table.Headers>

      <template v-if="showSkeleton">
        <Table.Row v-for="n in 7" :key="n" :id="n.toString()">
          <Table.Cell>
            <div class="w-6 h-6 flex items-center justify-center mr-2.5">
              <Skeleton class="w-[17px] h-6" />
            </div>
            <div>
              <Skeleton class="w-[100px] h-[13px] mb-1" />
              <Skeleton class="tablet-h:hidden w-[150px] h-[13px]" />
            </div>
          </Table.Cell>
          <Table.Cell class="hidden tablet-h:flex">
            <Skeleton class="w-[85px] h-[13px]" />
          </Table.Cell>
        </Table.Row>
      </template>

      <template v-else>
        <RecycleScroller
          v-if="files?.length"
          ref="container"
          :items="files"
          :item-size="itemSize"
          v-slot="{ item }: { item: Item }"
          class="h-full overflow-y-auto!"
        >
          <Table.Row
            :id="item.id"
            :class="getInactiveClasses(item)"
            @click="handleItemClick(item)"
          >
            <Table.Cell>
              <ThumbnailIcon
                class="mr-2.5 min-w-9 max-w-9 h-9 tablet-h:min-w-6 tablet-h:max-w-6 tablet-h:h-6"
                :icon-size="thumbnailSize"
                :type="getItemType(item)"
                :image-url="
                  applyAuthorizationToken(item.file?.imageUrl, [
                    { key: 'width', value: 36 },
                  ])
                "
              />
              <div v-truncate class="w-full">
                <div class="flex items-center">
                  <div data-truncate-target>{{ item.name }}</div>
                  <div
                    v-if="isCoOwner(item)"
                    v-tooltip="'Co-owned copyright'"
                    class="ml-2 w-[22px] h-[22px] bg-gray-800/5 rounded-sm flex items-center justify-center"
                  >
                    <Icon
                      name="CoOwner"
                      :size="14"
                      class="stroke-gray-800/60"
                    />
                  </div>
                  <div
                    v-if="isAuthor(item)"
                    v-tooltip="'Authored copyright'"
                    class="ml-2 w-[22px] h-[22px] bg-gray-800/5 rounded-sm flex items-center justify-center"
                  >
                    <Icon name="Author" :size="14" class="stroke-[#7B7C7E]" />
                  </div>
                </div>
                <div
                  class="tablet-h:hidden text-[13px] opacity-50 mt-0.5 cursor-pointer"
                  @click.stop="handlePathClick(item.parentPath)"
                >
                  {{ formatPath(item.parentPath) }}
                </div>
              </div>
            </Table.Cell>

            <Table.Cell class="hidden tablet-h:flex">
              <template v-if="item.file">
                <Icon name="Dacr" :size="18" class="mr-2" />
                <span class="font-secondary">
                  {{ formatTimestamp(item.file.timestamp) || '- -' }}
                </span>
              </template>
              <template v-else>- -</template>
            </Table.Cell>
          </Table.Row>
        </RecycleScroller>

        <div
          v-else
          class="w-full h-full flex flex-col items-center justify-center"
        >
          <div
            class="size-[126px] flex items-center justify-center bg-gray-200/30 rounded-[16px] mb-[43px]"
          >
            <Lottie name="Search-Results" class="min-w-[160px]" />
          </div>
          <div
            class="font-semibold font-primary text-[20px] leading-[20px] tracking-[-0.25px] text-gray-800 mb-4"
          >
            No results found
          </div>
          <div
            class="font-secondary text-[14px] leading-[22px] text-gray-800 max-w-[302px] text-center"
          >
            Try different keywords
          </div>
        </div>
      </template>
    </Table.Base>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue'
import {
  FileAuthorRole,
  FileOwnerRole,
  FileStatus,
  ItemType,
  SearchSort,
  useSearchItemsQuery,
  type Item,
} from '@/shared/graphql/legacy/generated'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { useDevice } from '@/shared/composables/useDevice'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { formatTimestamp } from '@/shared/utils/datetime'
import { Table, ThumbnailIcon, Skeleton, Icon } from '@dacr/ui'
import { TableTypes } from '@/shared/types'
import Lottie from '@/shared/components/Lottie.vue'

const route = useRoute()
const router = useRouter()
const device = useDevice()
const { availableHeight } = useAvailableHeight()
const { open } = useDialog()

useHead({
  title: `Search results in your files for "${route.query.query}" - Dacr`,
})

const sortBy = ref<SearchSort>(SearchSort.NameAsc)
const sortDirection = ref<'asc' | 'desc'>('asc')
const container = ref()

const variables = computed(() => ({
  query: route.query.query as string,
  sortBy: sortBy.value,
}))

const { result, loading, fetchMore } = useSearchItemsQuery(variables)

const componentStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const files = computed(
  () => result.value?.searchItems.files as Item[] | undefined
)

const showSkeleton = computed(
  () => files.value === null || files.value === undefined
)

const resultCopy = computed(() =>
  (result.value?.searchItems?.total || 0) > 1 ? 'results' : 'result'
)

const thumbnailSize = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 24 : 36
)

const itemSize = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 64 : 40
)

const headerIconClasses = computed(() => ({
  ...(sortDirection.value === 'desc' && { 'rotate-180': true }),
}))

const getItemType = (item: Item) =>
  item.type === ItemType.Folder ? ItemType.Folder : item.file!.type

const getInactiveClasses = (item: Item) => ({
  'opacity-50': item.file?.status === FileStatus.Inactive,
})

const isCoOwner = (item: Item) =>
  item.file?.ownershipRoles.includes(FileOwnerRole.Owner)

const isAuthor = (item: Item) =>
  item.file?.authorRoles.includes(FileAuthorRole.Author)

const formatPath = (path: string) => (path === '/' ? 'Dacr' : path)

const handleSort = (key: string) => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'

  if (key === 'name' && sortDirection.value === 'asc')
    sortBy.value = SearchSort.NameAsc
  if (key === 'name' && sortDirection.value === 'desc')
    sortBy.value = SearchSort.NameDesc
  if (key === 'timestamp' && sortDirection.value === 'asc')
    sortBy.value = SearchSort.TimestampAsc
  if (key === 'timestamp' && sortDirection.value === 'desc')
    sortBy.value = SearchSort.TimestampDesc
}

const handleItemClick = (item: Item) => {
  if (item.file?.status === FileStatus.Inactive) return

  if (item.type === ItemType.File) {
    router.push(`/file/${item.file?.copyrightId}`)
  } else {
    router.push(`/home${item.parentPath}/${item.name}`.replace('//', '/'))
  }
}

const handlePathClick = (path: string) => {
  if (path === '/') {
    router.push('/home')
  } else {
    router.push(`/home${path}`)
  }
}

const handleThreeDotsClick = (item: Item) => {
  if (item.type === ItemType.File) {
    open(Dialogs.FileActions, {
      props: { file: item.file },
    })
  } else {
    open(Dialogs.FolderActions, {
      props: { folder: item },
    })
  }
}

const fetchNextPage = () => {
  if (result.value?.searchItems.next && !loading.value) {
    fetchMore({
      variables: { next: result.value.searchItems.next },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData
        return {
          __typename: 'Query' as const,
          searchItems: {
            ...previousData.searchItems,
            files: [
              ...previousData.searchItems.files,
              ...newData.searchItems.files,
            ],
            next: newData.searchItems.next,
            total: newData.searchItems.total,
          },
        }
      },
    })
  }
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const scrollPos = element.clientHeight + element.scrollTop
  if (scrollPos / element.scrollHeight >= 0.5) {
    fetchNextPage()
  }
}

watchEffect(() => {
  container.value?.$el?.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  container.value?.$el?.removeEventListener('scroll', handleScroll)
})
</script>
